import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ApiError } from "../api";
import {
  apiTrafos,
  CHAVES_PLACA,
  CONCLUIDO,
  ENSAIOS_ENTRADA,
  ENSAIOS_FINAIS,
  ETAPAS,
  ETAPAS_INFO,
  SERVICO_ENCERRADO,
  STATUS_SERVICO,
  dataBR,
  dataHora,
  descricaoTrafo,
  duracao,
  entregaAtrasada,
  haQuanto,
  hojeYmd,
  indiceEtapa,
  infoEtapa,
  nomeEtapa,
  pendenciasDaEtapa,
  type ColetaOleo,
  type EntradaTrafo,
  type Ensaio,
  type Etapa,
  type FotoTrafo,
  type ItemInspecao,
  type Servico,
  type StatusServico,
  type Trafo,
} from "../trafos";
import {
  ChecklistInspecao,
  FormColeta,
  FormEntrada,
  FormPlaca,
  FotoAmpliada,
  Opcoes,
  PilulaServico,
  SeletorFotos,
  TabelaEnsaios,
  montarEnsaios,
  montarInspecao,
  type ServicoRascunho,
} from "./TrafoEtapas";
import { IconCheck, IconClose, IconFile, IconPlus, IconTrash } from "../icons";
import { imprimirRastreabilidade } from "../trafo-folha";

/**
 * FICHA DO TRAFO EM REFORMA — o trafo inteiro numa janela: os 5 passos no
 * topo, o formulário da etapa escolhida, as fotos dela, o que falta para
 * concluir e o histórico de tudo o que foi feito.
 *
 *   id = null  → "Nova entrada de trafo" (só a etapa 1; ao registrar, a ficha
 *                passa a ser a do trafo criado).
 *   gestao     → Gestão da Operação: corrige trafo concluído, reabre e exclui
 *                (excluir continua só para administrador — o servidor confere).
 *
 * Tempo real: a ficha aberta pergunta ao servidor a cada 15 s se o trafo mudou.
 * O que a pessoa está digitando (etapa com alteração não salva) NUNCA é
 * substituído — só as outras etapas e o andamento se atualizam.
 *
 * ARQUIVO IGUAL no sistemaColaborador e no sistemaOperacional.
 */

interface Props {
  id: number | null;
  onClose: () => void;
  /** Algo foi gravado: a lista de fora recarrega. */
  onChanged: () => void;
  somenteLeitura?: boolean;
  gestao?: boolean;
}

const VIGIA_MS = 15_000;

function entradaVazia(): EntradaTrafo {
  return {
    cliente: "",
    os_numero: "",
    nota_fiscal: "",
    identificacao: "",
    data_entrada: hojeYmd(),
    previsao_entrega: "",
    condicao_chegada: "",
  };
}

function entradaDe(t: Trafo): EntradaTrafo {
  return {
    cliente: t.cliente ?? "",
    os_numero: t.os_numero ?? "",
    nota_fiscal: t.nota_fiscal ?? "",
    identificacao: t.identificacao ?? "",
    data_entrada: t.data_entrada ?? "",
    previsao_entrega: t.previsao_entrega ?? "",
    condicao_chegada: t.condicao_chegada ?? "",
  };
}

const mensagem = (err: unknown) =>
  err instanceof ApiError ? err.message : "Falha de conexão. Confira a internet e tente de novo.";

/** Etapa que a ficha mostra para a etapa do trafo (concluído → Qualidade). */
const etapaVisivel = (e: string): Etapa => (e === CONCLUIDO ? "qualidade" : (e as Etapa));

/**
 * O que estava GRAVADO quando cada rascunho começou. O Salvar manda só a
 * diferença entre o rascunho e isto — nunca a etapa inteira, que apagaria o
 * que outra pessoa gravou nos campos em que esta não mexeu.
 */
interface Base {
  entrada: EntradaTrafo;
  placa: Record<string, unknown>;
  servicos: ServicoRascunho[];
  ensEntrada: Ensaio[];
  coleta: ColetaOleo;
  ensFinais: Ensaio[];
  inspecao: ItemInspecao[];
  parecerObs: string;
}

function baseVazia(): Base {
  return {
    entrada: entradaVazia(),
    placa: {},
    servicos: [],
    ensEntrada: montarEnsaios([], ENSAIOS_ENTRADA),
    coleta: {},
    ensFinais: montarEnsaios([], ENSAIOS_FINAIS),
    inspecao: montarInspecao([]),
    parecerObs: "",
  };
}

const igual = (x: unknown, y: unknown) => JSON.stringify(x ?? "") === JSON.stringify(y ?? "");

/** Linhas (ensaio / item) que mudaram em relação ao gravado, pelo nome. */
function linhasMudadas<T extends object>(atual: T[], base: T[], chaveLinha: keyof T): T[] {
  return atual.filter((x) => !igual(x, base.find((y) => y[chaveLinha] === x[chaveLinha])));
}

/** O Salvar não tem nada a mandar? */
function semMudanca(dados: Record<string, unknown>): boolean {
  return Object.values(dados).every((v) =>
    Array.isArray(v) ? v.length === 0 : v && typeof v === "object" ? Object.keys(v).length === 0 : false,
  );
}

export default function TrafoFicha({ id, onClose, onChanged, somenteLeitura = false, gestao = false }: Props) {
  const [idAtual, setIdAtual] = useState<number | null>(id);
  const [trafo, setTrafo] = useState<Trafo | null>(null);
  const [erroCarga, setErroCarga] = useState<string | null>(null);
  const [vendo, setVendo] = useState<Etapa>("entrada");

  // Rascunhos de cada etapa (ficam guardados ao trocar de etapa).
  const [entrada, setEntrada] = useState<EntradaTrafo>(entradaVazia);
  const [placa, setPlaca] = useState<Record<string, unknown>>({});
  const [servDef, setServDef] = useState<ServicoRascunho[]>([]);
  const [ensEntrada, setEnsEntrada] = useState<Ensaio[]>(() => montarEnsaios([], ENSAIOS_ENTRADA));
  const [coleta, setColeta] = useState<ColetaOleo>({});
  const [ensFinais, setEnsFinais] = useState<Ensaio[]>(() => montarEnsaios([], ENSAIOS_FINAIS));
  const [inspecao, setInspecao] = useState<ItemInspecao[]>(() => montarInspecao([]));
  const [parecerObs, setParecerObs] = useState("");
  /** Etapas com alteração ainda não salva. */
  const [sujas, setSujas] = useState<Set<Etapa>>(() => new Set());
  const baseRef = useRef<Base>(baseVazia());
  // Registro de andamento (etapa 4): fica na ficha, não no painel, para não
  // sumir quando a pessoa troca de passo ou a etapa muda.
  const [nota, setNota] = useState("");
  const [fotosNota, setFotosNota] = useState<string[]>([]);
  const registroPendente = nota.trim() !== "" || fotosNota.length > 0;
  /** Cada recarga feita por uma ação ganha um número: resposta atrasada do vigia é descartada. */
  const geracaoRef = useRef(0);

  const [ocupado, setOcupado] = useState(false);
  const [imprimindo, setImprimindo] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const [avisoFora, setAvisoFora] = useState<string | null>(null);
  const [devolucao, setDevolucao] = useState<{ para: Etapa; motivo: string } | null>(null);
  const [zoom, setZoom] = useState<string | null>(null);

  // Fotos da etapa aberta (as imagens só vêm quando a etapa é aberta).
  const [fotos, setFotos] = useState<FotoTrafo[]>([]);
  const [carregandoFotos, setCarregandoFotos] = useState(false);
  const [enviandoFotos, setEnviandoFotos] = useState(false);

  // Espelhos para o vigia (o intervalo não enxerga o estado novo sem eles).
  const trafoRef = useRef<Trafo | null>(null);
  const sujasRef = useRef<Set<Etapa>>(sujas);
  const ocupadoRef = useRef(false);
  const registroPendenteRef = useRef(false);
  trafoRef.current = trafo;
  sujasRef.current = sujas;
  ocupadoRef.current = ocupado || enviandoFotos;
  registroPendenteRef.current = registroPendente;

  // No celular os passos são uma faixa que rola de lado: o passo aberto vem
  // para o meio dela (sem mexer na rolagem da página).
  const passosRef = useRef<HTMLOListElement>(null);
  useEffect(() => {
    const faixa = passosRef.current;
    const passo = faixa?.querySelector<HTMLElement>(".trafo-passo.vendo");
    if (!faixa || !passo) return;
    faixa.scrollTo({ left: passo.offsetLeft - (faixa.clientWidth - passo.offsetWidth) / 2, behavior: "smooth" });
  }, [vendo, trafo?.etapa]);

  const sujar = (e: Etapa) =>
    setSujas((s) => {
      if (s.has(e)) return s;
      const n = new Set(s);
      n.add(e);
      return n;
    });

  /**
   * Aplica o trafo vindo do servidor. As etapas com alteração não salva ficam
   * como estão — exceto as de `limpar` (as que acabaram de ser gravadas).
   */
  const aplicar = useCallback((t: Trafo, opcoes: { limpar?: Etapa[]; seguirEtapa?: boolean } = {}) => {
    const anterior = trafoRef.current;
    const manter = new Set([...sujasRef.current].filter((e) => !(opcoes.limpar ?? []).includes(e)));
    const base = baseRef.current;
    if (!manter.has("entrada")) {
      base.entrada = entradaDe(t);
      setEntrada(base.entrada);
    }
    if (!manter.has("placa")) {
      base.placa = { ...(t.placa ?? {}) };
      base.servicos = (t.servicos ?? []).map((s) => ({ id: s.id, nome: s.nome, status: s.status }));
      setPlaca({ ...base.placa });
      setServDef(base.servicos.map((s) => ({ ...s })));
    } else {
      // Rascunho da placa aberto: o status dos serviços (que anda na etapa 4)
      // segue o banco, para a trava de "já iniciado" valer com o que é real.
      const status = new Map((t.servicos ?? []).map((s) => [s.id, s.status]));
      setServDef((l) => l.map((s) => (s.id && status.has(s.id) ? { ...s, status: status.get(s.id) } : s)));
    }
    if (!manter.has("ensaios")) {
      base.ensEntrada = montarEnsaios(t.ensaios_entrada, ENSAIOS_ENTRADA);
      base.coleta = { ...(t.coleta_oleo ?? {}) };
      setEnsEntrada(base.ensEntrada.map((e) => ({ ...e })));
      setColeta({ ...base.coleta });
    }
    if (!manter.has("qualidade")) {
      base.ensFinais = montarEnsaios(t.ensaios_finais, ENSAIOS_FINAIS);
      base.inspecao = montarInspecao(t.inspecao_final);
      base.parecerObs = t.parecer_obs ?? "";
      setEnsFinais(base.ensFinais.map((e) => ({ ...e })));
      setInspecao(base.inspecao.map((i) => ({ ...i })));
      setParecerObs(base.parecerObs);
    }
    setSujas(manter);
    setTrafo(t);
    const atual = etapaVisivel(t.etapa);
    setVendo((v) => {
      if (!anterior || opcoes.seguirEtapa) return atual;
      // Devolvido: a etapa aberta pode ter deixado de estar liberada.
      if (indiceEtapa(v) > indiceEtapa(t.etapa)) return atual;
      // Registro de andamento por enviar: não tira a pessoa da etapa 4.
      if (registroPendenteRef.current && v === "servicos") return v;
      // Quem olhava a etapa atual acompanha o trafo quando ele anda.
      if (anterior.etapa !== t.etapa && v === etapaVisivel(anterior.etapa)) return atual;
      return v;
    });
  }, []);

  const recarregar = useCallback(
    async (opcoes: { limpar?: Etapa[]; seguirEtapa?: boolean } = {}) => {
      const alvo = idAtual ?? trafoRef.current?.id;
      if (!alvo) return;
      geracaoRef.current += 1;
      aplicar(await apiTrafos.detalhe(alvo), opcoes);
    },
    [idAtual, aplicar],
  );

  // Primeira carga (e quando a "nova entrada" vira um trafo de verdade).
  useEffect(() => {
    if (idAtual === null) return;
    let vivo = true;
    setErroCarga(null);
    apiTrafos
      .detalhe(idAtual)
      .then((t) => {
        if (vivo) aplicar(t);
      })
      .catch((err) => {
        if (vivo) setErroCarga(mensagem(err));
      });
    return () => {
      vivo = false;
    };
  }, [idAtual, aplicar]);

  // Vigia: alguém mexeu neste trafo? (outra pessoa no campo, ou a gestão)
  useEffect(() => {
    if (idAtual === null) return;
    const timer = setInterval(async () => {
      if (document.visibilityState !== "visible" || ocupadoRef.current) return;
      const geracao = geracaoRef.current;
      try {
        const t = await apiTrafos.detalhe(idAtual);
        const atual = trafoRef.current;
        // Uma ação desta tela recarregou no meio do caminho: esta resposta pode
        // ser de antes da gravação — descarta (a próxima volta confere de novo).
        if (geracao !== geracaoRef.current || ocupadoRef.current) return;
        if (!atual || Date.parse(t.updated_at) <= Date.parse(atual.updated_at)) return;
        aplicar(t);
        setAvisoFora(
          `${t.atualizado_por || "Outra pessoa"} atualizou este trafo agora` +
            (sujasRef.current.size > 0 ? " — o que você está preenchendo foi mantido." : "."),
        );
      } catch {
        /* rede do celular oscila: tenta de novo na próxima volta */
      }
    }, VIGIA_MS);
    return () => clearInterval(timer);
  }, [idAtual, aplicar]);

  useEffect(() => {
    if (!avisoFora) return;
    const t = setTimeout(() => setAvisoFora(null), 9000);
    return () => clearTimeout(t);
  }, [avisoFora]);

  // Fotos da etapa aberta: só busca de novo quando a lista de fotos muda.
  const fotosMeta = useMemo(() => (trafo?.fotos ?? []).filter((f) => f.etapa === vendo), [trafo, vendo]);
  const chaveFotos = fotosMeta.map((f) => f.id).join(",");
  useEffect(() => {
    if (!idAtual || !chaveFotos) {
      setFotos([]);
      setCarregandoFotos(false);
      return;
    }
    let vivo = true;
    setCarregandoFotos(true);
    apiTrafos
      .fotos(idAtual, vendo)
      .then((f) => {
        if (vivo) setFotos(f);
      })
      .catch(() => {
        if (vivo) setFotos([]);
      })
      .finally(() => {
        if (vivo) setCarregandoFotos(false);
      });
    return () => {
      vivo = false;
    };
  }, [idAtual, vendo, chaveFotos]);

  /* ------------------------------------------------------ permissões --- */

  const concluido = trafo?.etapa === CONCLUIDO;
  const iAtual = trafo ? indiceEtapa(trafo.etapa) : 0;
  const etapaAtual: Etapa | null = trafo && !concluido ? (trafo.etapa as Etapa) : null;

  /** Pode mexer nos dados desta etapa? (o servidor confere de novo) */
  const podeEditar = (e: Etapa) =>
    !somenteLeitura && (!trafo || ((!concluido || gestao) && indiceEtapa(e) <= iAtual));
  const podeStatusServico = !somenteLeitura && !!trafo && (trafo.etapa === "servicos" || (gestao && !concluido));
  const podeAcrescentarServico =
    !somenteLeitura && !!trafo && ["placa", "ensaios", "servicos"].includes(trafo.etapa);
  const podeDevolver =
    !somenteLeitura && !!trafo && iAtual > 0 && vendo === etapaVisivel(trafo.etapa) && (!concluido || gestao);

  /* ---------------------------------------------- o que falta concluir --- */

  const pendencias = useMemo(() => {
    if (!trafo || !etapaAtual) return [];
    return pendenciasDaEtapa(
      {
        cliente: entrada.cliente,
        data_entrada: entrada.data_entrada,
        placa,
        servicos:
          etapaAtual === "placa"
            ? servDef.map((s) => ({ id: s.id ?? s.nome, nome: s.nome, status: (s.status ?? "Pendente") as StatusServico, obs: "", atualizado_por: null, atualizado_em: null }))
            : trafo.servicos,
        ensaios_entrada: ensEntrada,
        coleta_oleo: coleta,
        ensaios_finais: ensFinais,
        inspecao_final: inspecao,
      },
      etapaAtual,
    );
  }, [trafo, etapaAtual, entrada, placa, servDef, ensEntrada, coleta, ensFinais, inspecao]);

  /* ----------------------------------------------------------- ações --- */

  /** Só o que a pessoa mudou nesta etapa (o servidor junta com o que já está gravado). */
  function dadosDaEtapa(e: Etapa): Record<string, unknown> | null {
    const b = baseRef.current;
    switch (e) {
      case "entrada": {
        const mud: Record<string, unknown> = {};
        for (const k of Object.keys(entrada) as (keyof EntradaTrafo)[]) {
          if (entrada[k] !== b.entrada[k]) mud[k] = entrada[k];
        }
        return mud;
      }
      case "placa": {
        const mud: Record<string, unknown> = {};
        for (const k of [...CHAVES_PLACA, "sem_placa"]) {
          if (!igual(placa[k], b.placa[k])) mud[k] = placa[k] ?? (k === "sem_placa" ? false : "");
        }
        const ficaram = new Set(servDef.map((s) => s.id).filter(Boolean));
        return {
          placa: mud,
          servicos_adicionar: servDef.filter((s) => !s.id).map((s) => s.nome),
          servicos_remover: b.servicos.filter((s) => s.id && !ficaram.has(s.id)).map((s) => s.id),
        };
      }
      case "ensaios": {
        const col: Record<string, unknown> = {};
        const c = coleta as Record<string, unknown>;
        const cb = b.coleta as Record<string, unknown>;
        for (const k of new Set([...Object.keys(c), ...Object.keys(cb)])) {
          if (!igual(c[k], cb[k])) col[k] = c[k] ?? null;
        }
        return { ensaios_entrada: linhasMudadas(ensEntrada, b.ensEntrada, "ensaio"), coleta_oleo: col };
      }
      case "qualidade": {
        const dados: Record<string, unknown> = {
          ensaios_finais: linhasMudadas(ensFinais, b.ensFinais, "ensaio"),
          inspecao_final: linhasMudadas(inspecao, b.inspecao, "item"),
        };
        if (parecerObs !== b.parecerObs) dados.parecer_obs = parecerObs;
        return dados;
      }
      default:
        return null;
    }
  }

  /** Grava o rascunho da etapa (só o que mudou). Devolve false se não havia nada. */
  async function gravarEtapa(id: number, e: Etapa): Promise<boolean> {
    const dados = dadosDaEtapa(e);
    if (!dados || semMudanca(dados)) return false;
    await apiTrafos.salvar(id, e, dados);
    return true;
  }

  async function executar(fn: () => Promise<void>) {
    setOcupado(true);
    setErro(null);
    setAviso(null);
    try {
      await fn();
    } catch (err) {
      setErro(mensagem(err));
    } finally {
      setOcupado(false);
    }
  }

  async function registrarEntrada() {
    if (!entrada.cliente.trim()) return setErro("Informe o cliente.");
    if (!entrada.data_entrada) return setErro("Informe a data de entrada.");
    await executar(async () => {
      const r = await apiTrafos.criar(entrada);
      setAviso("Entrada registrada. Fotografe o trafo e conclua a etapa quando estiver tudo conferido.");
      // Gravado: o que foi digitado deixa de ser "não salvo".
      setSujas(new Set());
      setIdAtual(r.id);
      onChanged();
    });
  }

  async function salvar() {
    if (!trafo) return;
    await executar(async () => {
      const gravou = await gravarEtapa(trafo.id, vendo);
      await recarregar({ limpar: [vendo] });
      setAviso(gravou ? "Salvo." : "Nada mudou nesta etapa.");
      if (gravou) onChanged();
    });
  }

  async function concluirEtapa() {
    if (!trafo || !etapaAtual) return;
    await executar(async () => {
      if (sujas.has(etapaAtual)) {
        await gravarEtapa(trafo.id, etapaAtual);
        await recarregar({ limpar: [etapaAtual] });
      }
      try {
        const r = await apiTrafos.avancar(trafo.id, etapaAtual);
        await recarregar({ seguirEtapa: true });
        setAviso(
          r.etapa === CONCLUIDO
            ? "Trafo aprovado pela Qualidade e concluído."
            : `Etapa concluída. Agora: ${nomeEtapa(r.etapa)}.`,
        );
        onChanged();
      } catch (err) {
        // Outra pessoa andou com o trafo: mostra onde ele está agora.
        if (err instanceof ApiError && err.message.includes("mudou de etapa")) {
          await recarregar({ seguirEtapa: true }).catch(() => {});
        }
        throw err;
      }
    });
  }

  function abrirDevolucao() {
    if (!trafo) return;
    const para: Etapa = trafo.etapa === "qualidade" || concluido ? "servicos" : ETAPAS[Math.max(0, iAtual - 1)]!;
    setDevolucao({ para, motivo: "" });
  }

  async function confirmarDevolucao() {
    if (!trafo || !devolucao) return;
    if (devolucao.motivo.trim().length < 3) return setErro("Explique o motivo da devolução.");
    const d = devolucao;
    await executar(async () => {
      // O que foi preenchido na etapa atual (a inspeção que reprovou, por
      // exemplo) é gravado ANTES de devolver — senão a etapa fica travada e o
      // registro da reprovação some com o rascunho.
      if (etapaAtual && sujas.has(etapaAtual)) {
        await gravarEtapa(trafo.id, etapaAtual);
        await recarregar({ limpar: [etapaAtual] });
      }
      try {
        await apiTrafos.devolver(trafo.id, d.para, d.motivo.trim(), trafo.etapa);
      } catch (err) {
        if (err instanceof ApiError && err.message.includes("mudou de etapa")) {
          setDevolucao(null);
          await recarregar({ seguirEtapa: true }).catch(() => {});
        }
        throw err;
      }
      setDevolucao(null);
      await recarregar({ seguirEtapa: true });
      setAviso(`Trafo ${concluido ? "reaberto" : "devolvido"} para ${nomeEtapa(d.para)}.`);
      onChanged();
    });
  }

  async function excluir() {
    if (!trafo) return;
    const ok = window.confirm(
      `Excluir o Trafo nº ${trafo.id} (${trafo.cliente})? Some também o histórico e as fotos. Não dá para desfazer.`,
    );
    if (!ok) return;
    await executar(async () => {
      await apiTrafos.excluir(trafo.id);
      onChanged();
      onClose();
    });
  }

  /**
   * Relatório de rastreabilidade (imprimir ou salvar em PDF). Sem `await` antes
   * de abrir a janela: imprimirRastreabilidade a abre dentro do clique, senão o
   * navegador bloqueia como pop-up.
   */
  function imprimir() {
    if (!trafo) return;
    setImprimindo(true);
    setErro(null);
    imprimirRastreabilidade(trafo.id)
      .then((falha) => {
        if (falha) setErro(falha);
        else if (sujasRef.current.size > 0) {
          setAviso("O relatório saiu com o que já está gravado — as alterações ainda não salvas não entraram.");
        }
      })
      .finally(() => setImprimindo(false));
  }

  async function enviarFotos(urls: string[]) {
    if (!trafo) return;
    setEnviandoFotos(true);
    setErro(null);
    try {
      await apiTrafos.enviarFotos(trafo.id, vendo, urls);
      await recarregar();
      onChanged();
    } catch (err) {
      setErro(mensagem(err));
    } finally {
      setEnviandoFotos(false);
    }
  }

  async function apagarFoto(fotoId: number) {
    if (!trafo || !window.confirm("Apagar esta foto?")) return;
    await executar(async () => {
      await apiTrafos.apagarFoto(trafo.id, fotoId);
      await recarregar();
      onChanged();
    });
  }

  const fechar = useCallback(() => {
    const pendente = sujasRef.current.size > 0 || registroPendenteRef.current;
    if (pendente && !window.confirm("Há alterações não salvas. Fechar mesmo assim?")) return;
    onClose();
  }, [onClose]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      if (devolucao) setDevolucao(null);
      else fechar();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [devolucao, fechar]);

  /* ------------------------------------------------------------ tela --- */

  const info = infoEtapa(vendo)!;
  const ehAtualVendo = !!etapaAtual && vendo === etapaAtual;
  const temForm = vendo !== "servicos";
  // Última mudança de etapa foi uma devolução? Mostra o motivo na etapa atual.
  const ultimaMudanca = trafo?.eventos.find((e) => ["etapa", "devolucao", "conclusao"].includes(e.tipo));
  const devolvido = ultimaMudanca?.tipo === "devolucao" ? ultimaMudanca : null;

  const titulo = trafo ? `Trafo nº ${trafo.id} — ${trafo.cliente}` : idAtual ? "Carregando…" : "Nova entrada de trafo";

  return (
    <div className="modal-overlay trafo-overlay" onMouseDown={fechar}>
      <div className="modal-card trafo-modal" onMouseDown={(e) => e.stopPropagation()} role="dialog" aria-label={titulo}>
        <div className="modal-head">
          <div className="trafo-modal-titulo">
            <h3>{titulo}</h3>
            {trafo && (
              <p className="muted">
                {descricaoTrafo({
                  fabricante: trafo.placa.fabricante as string,
                  potencia_kva: trafo.placa.potencia_kva as string,
                  numero_serie: trafo.placa.numero_serie as string,
                  identificacao: trafo.identificacao,
                })}
                {trafo.os_numero ? ` · OS ${trafo.os_numero}` : ""}
              </p>
            )}
          </div>
          <button type="button" className="icon-btn" onClick={fechar} aria-label="Fechar">
            <IconClose size={18} />
          </button>
        </div>

        {trafo && (
          <ol className="trafo-passos" ref={passosRef}>
            {ETAPAS_INFO.map((p) => {
              const i = p.numero - 1;
              const estado = i < iAtual ? "feito" : i === iAtual ? "atual" : "futuro";
              return (
                <li key={p.key}>
                  <button
                    type="button"
                    className={`trafo-passo ${estado}${vendo === p.key ? " vendo" : ""}`}
                    disabled={estado === "futuro"}
                    onClick={() => {
                      setVendo(p.key);
                      setErro(null);
                      setAviso(null);
                    }}
                    title={estado === "futuro" ? "Etapa ainda não liberada" : p.titulo}
                  >
                    <span className="trafo-passo-num">{estado === "feito" ? <IconCheck size={13} /> : p.numero}</span>
                    <span className="trafo-passo-nome">{p.curto}</span>
                    {sujas.has(p.key) && (
                      <span className="trafo-passo-sujo" title="Alterações não salvas" aria-label="não salvo" />
                    )}
                  </button>
                </li>
              );
            })}
          </ol>
        )}

        <div className="modal-body trafo-corpo">
          {erroCarga ? (
            <div className="form-error">{erroCarga}</div>
          ) : idAtual !== null && !trafo ? (
            <div className="empty">Carregando…</div>
          ) : !trafo ? (
            /* ------------------------------ nova entrada ------------------------------ */
            <>
              <p className="trafo-ajuda">{infoEtapa("entrada")!.ajuda}</p>
              <fieldset className="trafo-travado" disabled={somenteLeitura || ocupado}>
                <FormEntrada
                  valor={entrada}
                  onChange={(v) => {
                    setEntrada(v);
                    sujar("entrada");
                  }}
                />
              </fieldset>
            </>
          ) : (
            <>
              <div className="trafo-resumo">
                <span>
                  {concluido ? (
                    <strong>Concluído</strong>
                  ) : (
                    <>
                      Etapa atual: <strong>{nomeEtapa(trafo.etapa)}</strong> · há {duracao(trafo.etapa_desde)}
                    </>
                  )}
                </span>
                <span>Entrada {dataBR(trafo.data_entrada)}</span>
                {trafo.previsao_entrega && (
                  <span className={entregaAtrasada(trafo) ? "trafo-atrasado" : undefined}>
                    Entrega prevista {dataBR(trafo.previsao_entrega)}
                    {entregaAtrasada(trafo) ? " (vencida)" : ""}
                  </span>
                )}
                <span className="muted">
                  Atualizado {haQuanto(trafo.updated_at)}
                  {trafo.atualizado_por ? ` por ${trafo.atualizado_por}` : ""}
                </span>
              </div>
              {avisoFora && <div className="trafo-aviso-fora">{avisoFora}</div>}

              <div className="trafo-etapa-cab">
                <div>
                  <h4>
                    {info.numero}. {info.titulo}
                  </h4>
                  <p className="muted">{info.ajuda}</p>
                </div>
                <span className={`pill ${ehAtualVendo ? "pill-andamento" : "pill-neutral"}`}>
                  {ehAtualVendo ? "Etapa atual" : indiceEtapa(vendo) < iAtual ? "Concluída" : ""}
                </span>
              </div>

              {devolvido && ehAtualVendo && (
                <div className="trafo-devolvido">
                  <strong>Devolvido</strong> por {devolvido.usuario ?? "—"} em {dataHora(devolvido.created_at)} —{" "}
                  {devolvido.texto}
                </div>
              )}
              {vendo === "qualidade" && concluido && (
                <div className="trafo-aprovado">
                  <IconCheck size={16} /> Aprovado pela Qualidade em {dataHora(trafo.data_conclusao)}
                  {trafo.responsavel_qualidade ? ` por ${trafo.responsavel_qualidade}` : ""}.
                </div>
              )}

              {/* ----------------------------- formulário da etapa ----------------------------- */}
              {temForm && (
                <fieldset className="trafo-travado" disabled={!podeEditar(vendo) || ocupado}>
                  {vendo === "entrada" && (
                    <FormEntrada
                      valor={entrada}
                      onChange={(v) => {
                        setEntrada(v);
                        sujar("entrada");
                      }}
                    />
                  )}
                  {vendo === "placa" && (
                    <FormPlaca
                      placa={placa}
                      onPlaca={(p) => {
                        setPlaca(p);
                        sujar("placa");
                      }}
                      servicos={servDef}
                      onServicos={(s) => {
                        setServDef(s);
                        sujar("placa");
                      }}
                      onErro={setErro}
                    />
                  )}
                  {vendo === "ensaios" && (
                    <>
                      <h4 className="trafo-secao">Ensaios elétricos de entrada</h4>
                      <TabelaEnsaios
                        ensaios={ensEntrada}
                        onChange={(e) => {
                          setEnsEntrada(e);
                          sujar("ensaios");
                        }}
                      />
                      <h4 className="trafo-secao">Coleta de óleo</h4>
                      <FormColeta
                        coleta={coleta}
                        trafoId={trafo.id}
                        onChange={(c) => {
                          setColeta(c);
                          sujar("ensaios");
                        }}
                      />
                    </>
                  )}
                  {vendo === "qualidade" && (
                    <>
                      <h4 className="trafo-secao">Ensaios finais</h4>
                      <TabelaEnsaios
                        ensaios={ensFinais}
                        onChange={(e) => {
                          setEnsFinais(e);
                          sujar("qualidade");
                        }}
                      />
                      <h4 className="trafo-secao">Inspeção final</h4>
                      <ChecklistInspecao
                        itens={inspecao}
                        onChange={(i) => {
                          setInspecao(i);
                          sujar("qualidade");
                        }}
                      />
                      <label className="field trafo-espaco">
                        <span>Observações da Qualidade</span>
                        <textarea
                          rows={3}
                          value={parecerObs}
                          onChange={(e) => {
                            setParecerObs(e.target.value);
                            sujar("qualidade");
                          }}
                        />
                      </label>
                    </>
                  )}
                </fieldset>
              )}

              {vendo === "servicos" && (
                <PainelServicos
                  trafo={trafo}
                  podeStatus={podeStatusServico}
                  podeAcrescentar={podeAcrescentarServico}
                  onGravado={async () => {
                    await recarregar();
                    onChanged();
                  }}
                  onErro={setErro}
                  onAmpliar={setZoom}
                  nota={nota}
                  onNota={setNota}
                  fotosNota={fotosNota}
                  onFotosNota={setFotosNota}
                />
              )}

              {/* ------------------------------------ fotos ------------------------------------ */}
              <section className="trafo-fotos">
                <h4 className="trafo-secao">
                  Fotos — {info.curto} <span className="muted">({fotosMeta.length})</span>
                </h4>
                {podeEditar(vendo) && (
                  <SeletorFotos onFotos={enviarFotos} onErro={setErro} ocupado={enviandoFotos} />
                )}
                {carregandoFotos && fotos.length === 0 ? (
                  <p className="muted">Carregando fotos…</p>
                ) : fotosMeta.length === 0 ? (
                  <p className="muted">Nenhuma foto nesta etapa.</p>
                ) : (
                  <ul className="trafo-fotos-lista">
                    {fotos.filter((f) => f.etapa === vendo).map((f) => {
                      const meta = fotosMeta.find((m) => m.id === f.id);
                      return (
                        <li key={f.id} className="trafo-foto">
                          <button type="button" className="trafo-foto-abrir" onClick={() => setZoom(f.url)}>
                            <img src={f.url} alt={f.legenda ?? "Foto do trafo"} />
                          </button>
                          {meta?.pode_apagar && !somenteLeitura && (
                            <button
                              type="button"
                              className="trafo-foto-apagar"
                              onClick={() => apagarFoto(f.id)}
                              title="Apagar esta foto"
                              aria-label="Apagar esta foto"
                            >
                              <IconTrash size={13} />
                            </button>
                          )}
                          {(f.legenda || meta?.usuario) && (
                            <span className="trafo-foto-legenda">
                              {f.legenda ?? ""}
                              {meta?.usuario ? `${f.legenda ? " · " : ""}${meta.usuario}` : ""}
                            </span>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                )}
              </section>

              {ehAtualVendo && pendencias.length > 0 && !somenteLeitura && (
                <div className="trafo-pendencias">
                  <strong>Para concluir esta etapa:</strong>
                  <ul>
                    {pendencias.map((p) => (
                      <li key={p}>{p}</li>
                    ))}
                  </ul>
                </div>
              )}

              <details className="trafo-historico">
                <summary>Histórico do trafo ({trafo.eventos.length})</summary>
                <ol>
                  {trafo.eventos.map((e) => (
                    <li key={e.id}>
                      <span className="trafo-hist-quando">{dataHora(e.created_at)}</span>
                      <span className="trafo-hist-texto">
                        {e.texto}
                        <span className="muted"> — {e.usuario ?? "—"}</span>
                      </span>
                    </li>
                  ))}
                </ol>
              </details>
            </>
          )}
        </div>

        <div className="modal-foot trafo-rodape">
          {erro ? (
            <div className="form-error">{erro}</div>
          ) : aviso ? (
            <div className="form-ok">{aviso}</div>
          ) : (
            <span className="trafo-rodape-vazio" />
          )}
          {!trafo ? (
            <>
              <button type="button" className="btn-secondary" onClick={fechar} disabled={ocupado}>
                Cancelar
              </button>
              {!somenteLeitura && idAtual === null && (
                <button type="button" className="btn-primary" onClick={registrarEntrada} disabled={ocupado}>
                  {ocupado ? "Registrando…" : "Registrar entrada"}
                </button>
              )}
            </>
          ) : (
            <>
              <button
                type="button"
                className="btn-secondary trafo-btn-imprimir"
                onClick={imprimir}
                disabled={imprimindo}
                title="Relatório de rastreabilidade: os dados de cada etapa, a passagem pelas etapas, o histórico completo e as fotos — para imprimir ou salvar em PDF"
              >
                <IconFile size={15} /> {imprimindo ? "Montando…" : "Imprimir / PDF"}
              </button>
              {gestao && trafo.pode_excluir && !somenteLeitura && (
                <button type="button" className="btn-secondary trafo-btn-excluir" onClick={excluir} disabled={ocupado}>
                  Excluir trafo
                </button>
              )}
              {podeDevolver && (
                <button type="button" className="btn-secondary" onClick={abrirDevolucao} disabled={ocupado}>
                  {concluido ? "Reabrir" : trafo.etapa === "qualidade" ? "Reprovar e devolver" : "Devolver etapa"}
                </button>
              )}
              {temForm && podeEditar(vendo) && (
                <button type="button" className="btn-secondary" onClick={salvar} disabled={ocupado || !sujas.has(vendo)}>
                  Salvar
                </button>
              )}
              {ehAtualVendo && !somenteLeitura && (
                <button
                  type="button"
                  className="btn-primary"
                  onClick={concluirEtapa}
                  disabled={ocupado || pendencias.length > 0}
                  title={pendencias.length > 0 ? pendencias.join(" ") : undefined}
                >
                  {ocupado ? "Gravando…" : vendo === "qualidade" ? "Aprovar e concluir" : "Concluir etapa"}
                </button>
              )}
            </>
          )}
        </div>
      </div>

      {devolucao && trafo && (
        <div className="modal-overlay" onMouseDown={(e) => { e.stopPropagation(); setDevolucao(null); }}>
          <div className="modal-card" onMouseDown={(e) => e.stopPropagation()}>
            <div className="modal-head">
              <h3>{concluido ? "Reabrir o trafo" : "Devolver o trafo"}</h3>
              <button type="button" className="icon-btn" onClick={() => setDevolucao(null)} aria-label="Fechar">
                <IconClose size={18} />
              </button>
            </div>
            <div className="modal-body">
              <label className="field">
                <span>Voltar para</span>
                <select
                  value={devolucao.para}
                  onChange={(e) => setDevolucao({ ...devolucao, para: e.target.value as Etapa })}
                >
                  {ETAPAS_INFO.filter((p) => p.numero - 1 < iAtual).map((p) => (
                    <option key={p.key} value={p.key}>
                      {p.numero}. {p.curto}
                    </option>
                  ))}
                </select>
              </label>
              <label className="field trafo-espaco">
                <span>
                  Motivo <em className="req">*</em>
                </span>
                <textarea
                  rows={3}
                  autoFocus
                  value={devolucao.motivo}
                  onChange={(e) => setDevolucao({ ...devolucao, motivo: e.target.value })}
                  placeholder={
                    trafo.etapa === "qualidade"
                      ? "O que reprovou na inspeção / ensaio e o que precisa ser refeito"
                      : "Por que o trafo está voltando"
                  }
                />
              </label>
            </div>
            <div className="modal-foot">
              <button type="button" className="btn-secondary" onClick={() => setDevolucao(null)} disabled={ocupado}>
                Cancelar
              </button>
              <button type="button" className="btn-primary" onClick={confirmarDevolucao} disabled={ocupado}>
                {concluido ? "Reabrir" : "Devolver"}
              </button>
            </div>
          </div>
        </div>
      )}

      {zoom && <FotoAmpliada url={zoom} onClose={() => setZoom(null)} />}
    </div>
  );
}

/* ------------------------------------------------- etapa 4: serviços --- */

function PainelServicos({
  trafo,
  podeStatus,
  podeAcrescentar,
  onGravado,
  onErro,
  onAmpliar,
  nota,
  onNota,
  fotosNota,
  onFotosNota,
}: {
  trafo: Trafo;
  podeStatus: boolean;
  podeAcrescentar: boolean;
  onGravado: () => Promise<void>;
  onErro: (msg: string | null) => void;
  onAmpliar: (url: string) => void;
  nota: string;
  onNota: (v: string) => void;
  fotosNota: string[];
  onFotosNota: (f: string[] | ((l: string[]) => string[])) => void;
}) {
  const [gravando, setGravando] = useState<string | null>(null);
  const [novo, setNovo] = useState("");
  const setNota = onNota;
  const setFotosNota = onFotosNota;
  /** Observação sendo digitada, por serviço (só enquanto não foi gravada). */
  const [obsEdit, setObsEdit] = useState<Record<string, string>>({});
  const [registrando, setRegistrando] = useState(false);
  const servicos = trafo.servicos ?? [];
  const encerrados = servicos.filter((s) => SERVICO_ENCERRADO.includes(s.status)).length;

  async function mudar(s: Servico, status: StatusServico, obs?: string) {
    setGravando(s.id);
    onErro(null);
    try {
      await apiTrafos.statusServico(trafo.id, s.id, status, obs);
      await onGravado();
    } catch (err) {
      onErro(mensagem(err));
    } finally {
      setGravando(null);
    }
  }

  /** Grava SÓ a observação — o status fica como está no banco. */
  async function gravarObs(s: Servico) {
    const digitado = obsEdit[s.id];
    if (digitado === undefined) return;
    const obs = digitado.trim();
    const limpar = () =>
      setObsEdit((m) => {
        const { [s.id]: _, ...resto } = m;
        return resto;
      });
    if (obs === (s.obs ?? "")) return limpar();
    setGravando(s.id);
    onErro(null);
    try {
      await apiTrafos.obsServico(trafo.id, s.id, obs);
      limpar();
      await onGravado();
    } catch (err) {
      onErro(mensagem(err));
    } finally {
      setGravando(null);
    }
  }

  async function acrescentar() {
    const nome = novo.trim();
    if (!nome) return;
    setGravando("novo");
    onErro(null);
    try {
      await apiTrafos.adicionarServico(trafo.id, nome);
      setNovo("");
      await onGravado();
    } catch (err) {
      onErro(mensagem(err));
    } finally {
      setGravando(null);
    }
  }

  async function registrar() {
    if (!nota.trim() && fotosNota.length === 0) return onErro("Escreva o que foi feito ou anexe uma foto.");
    setRegistrando(true);
    onErro(null);
    try {
      await apiTrafos.registro(trafo.id, "servicos", nota.trim(), fotosNota);
      setNota("");
      setFotosNota([]);
      await onGravado();
    } catch (err) {
      onErro(mensagem(err));
    } finally {
      setRegistrando(false);
    }
  }

  return (
    <>
      <h4 className="trafo-secao">
        Serviços{" "}
        <span className="muted">
          ({encerrados} de {servicos.length} encerrado{servicos.length === 1 ? "" : "s"})
        </span>
      </h4>
      {servicos.length > 0 && (
        <div className="trafo-progresso" aria-hidden="true">
          <span style={{ width: `${Math.round((encerrados / servicos.length) * 100)}%` }} />
        </div>
      )}
      {servicos.length === 0 ? (
        <p className="muted">Nenhum serviço definido (etapa 2).</p>
      ) : (
        <div className="trafo-itens">
          {servicos.map((s) => (
            <div key={s.id} className="trafo-item trafo-item-servico">
              <div className="trafo-item-nome">
                {s.nome}
                {s.atualizado_por && s.status !== "Pendente" && (
                  <span className="trafo-item-quem">
                    {s.status} por {s.atualizado_por} · {haQuanto(s.atualizado_em)}
                  </span>
                )}
              </div>
              {podeStatus ? (
                <Opcoes
                  opcoes={STATUS_SERVICO}
                  valor={s.status}
                  onEscolher={(v) => v !== s.status && mudar(s, v as StatusServico)}
                  travado={gravando !== null}
                  compacto
                />
              ) : (
                <PilulaServico s={s} />
              )}
              {podeStatus ? (
                <input
                  className="trafo-item-campo"
                  value={obsEdit[s.id] ?? s.obs ?? ""}
                  placeholder="Observação (grava ao sair do campo)"
                  onChange={(e) => setObsEdit((m) => ({ ...m, [s.id]: e.target.value }))}
                  onBlur={() => gravarObs(s)}
                />
              ) : (
                s.obs && <span className="trafo-item-obs">{s.obs}</span>
              )}
            </div>
          ))}
        </div>
      )}

      {podeAcrescentar && (
        <div className="trafo-linha-acrescentar">
          <input
            value={novo}
            onChange={(e) => setNovo(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                acrescentar();
              }
            }}
            placeholder="Serviço que apareceu durante a reforma"
          />
          <button type="button" className="btn-secondary btn-sm" onClick={acrescentar} disabled={!novo.trim() || gravando !== null}>
            <IconPlus size={14} /> Acrescentar serviço
          </button>
        </div>
      )}

      {podeStatus && (
        <div className="trafo-registro">
          <h4 className="trafo-secao">Registrar andamento</h4>
          <textarea
            rows={2}
            value={nota}
            onChange={(e) => setNota(e.target.value)}
            placeholder="O que foi feito hoje, o que foi encontrado, o que falta…"
          />
          {fotosNota.length > 0 && (
            <ul className="trafo-fotos-lista compacta">
              {fotosNota.map((url, i) => (
                <li key={i} className="trafo-foto">
                  <button type="button" className="trafo-foto-abrir" onClick={() => onAmpliar(url)}>
                    <img src={url} alt={`Foto ${i + 1}`} />
                  </button>
                  <button
                    type="button"
                    className="trafo-foto-apagar"
                    onClick={() => setFotosNota((l) => l.filter((_, p) => p !== i))}
                    aria-label="Tirar esta foto do registro"
                  >
                    <IconTrash size={13} />
                  </button>
                </li>
              ))}
            </ul>
          )}
          <div className="trafo-registro-acoes">
            <SeletorFotos
              onFotos={(urls) => setFotosNota((l) => [...l, ...urls].slice(0, 10))}
              onErro={(m) => onErro(m)}
              travado={registrando}
            />
            <button
              type="button"
              className="btn-primary btn-sm"
              onClick={registrar}
              disabled={registrando || (!nota.trim() && fotosNota.length === 0)}
            >
              {registrando ? "Registrando…" : "Registrar andamento"}
            </button>
          </div>
        </div>
      )}
    </>
  );
}
